import * as Api from '@ViewModels';
import {
	AIPromptBox,
	AIPromptBoxCredit,
	AIPromptBoxTextarea,
	AIPromptBoxToolbar,
	AIPromptBoxToolbarEnd,
} from '@HoudiniUI/ai/AIPromptBox';
import { FieldError } from '@HoudiniUI/Field';
import { InputGroupButton } from '@HoudiniUI/InputGroup';
import { Pill } from '@HoudiniUI/Pill';
import { PlusIcon2 } from '@WebComponents/svgs/icons/PlusIcon';
import { StyleDeclarationValue } from 'aphrodite';
import { useEffect, useRef, useState } from 'react';
import {
	AIPromptUsage,
	IAIPromptTemplate,
	findPromptPlaceholder,
	findPromptPlaceholders,
	getAIPromptText,
} from '../../../../models/AIPromptTemplate';
import { AIPromptPanel } from './AIPromptPanel';
import { HelpMeWriteMenu } from './HelpMeWriteMenu';
import { SavePromptDialog } from './SavePromptDialog';

export interface IAIPromptFieldCopy {
	attributionNote: string;
	panelDescription: string;
	/** The panel's description once the field holds text, which using a prompt would replace. */
	panelReplaceDescription: string;
	saveDescription: string;
	saveNamePlaceholder: string;
	saveNote?: string;
	savedEmptyText: string;
}

const DEFAULT_COPY: IAIPromptFieldCopy = {
	attributionNote: 'Editing the text drops this.',
	panelDescription: 'Using one fills the field.',
	panelReplaceDescription: 'Using one replaces what you have written.',
	saveDescription: 'Keep this so you can start from it next time.',
	saveNamePlaceholder: 'A name you will recognize',
	savedEmptyText: 'Nobody here has saved a prompt yet. Write one and use Save as a prompt beside it.',
};

const getPlaceholderProblem = (placeholder: string) => `Replace ${placeholder} with your own words before continuing.`;

/**
 * The state behind an AIPromptField: the value stays the caller's, while which prompt it started from and that
 * prompt's [placeholders] are kept here. Returned rather than reported, so a caller can read them in the same render
 * that builds its own output.
 */
export function useAIPromptField({
	onChange,
	onPromptApplied,
	value,
}: {
	onChange: (value: string) => void;
	onPromptApplied?: (prompt: IAIPromptTemplate) => void;
	value: string;
}) {
	const ref = useRef<HTMLTextAreaElement>(null);
	const [appliedPrompt, setAppliedPrompt] = useState<IAIPromptTemplate | null>(null);
	// Kept apart from appliedPrompt, which a save replaces with the user's own words: brackets they typed must never
	// become blanks they are made to fill.
	const [promptPlaceholders, setPromptPlaceholders] = useState<string[]>([]);
	// Set when a prompt is used, and applied once the value holds its text: the value has to be in the field before a
	// selection inside it means anything.
	const [pendingSelection, setPendingSelection] = useState<{ end: number; start: number } | null>(null);

	const applyPrompt = (prompt: IAIPromptTemplate) => {
		const text = getAIPromptText(prompt);
		onChange(text);
		setAppliedPrompt(prompt);
		setPromptPlaceholders(findPromptPlaceholders(text));
		// The first [placeholder] is what the user has to fill in, so it is selected ready to type over. A prompt
		// without one leaves the cursor at the end.
		setPendingSelection(findPromptPlaceholder(text) ?? { end: text.length, start: text.length });
		onPromptApplied?.(prompt);
	};

	useEffect(() => {
		if (!pendingSelection) {
			return;
		}
		ref.current?.focus();
		ref.current?.setSelectionRange(pendingSelection.start, pendingSelection.end);
		setPendingSelection(null);
	}, [pendingSelection]);

	// Left in, a blank reaches the AI as an instruction ("[describe what is new]") and it invents something to fill it.
	const unfilledPlaceholder = promptPlaceholders.find(placeholder => value.includes(placeholder)) ?? null;

	// Derived rather than stored so it clears itself the moment the value stops being the prompt's words, instead of
	// relying on every edit path remembering to reset it.
	const sourcePrompt = appliedPrompt && value.trim() === getAIPromptText(appliedPrompt) ? appliedPrompt : null;

	return {
		applyPrompt,
		onChange,
		/** A save makes the value that prompt's words, so it is credited to it and there is nothing left to save. */
		onSaved: setAppliedPrompt,
		placeholderProblem: unfilledPlaceholder ? getPlaceholderProblem(unfilledPlaceholder) : null,
		ref,
		sourcePrompt,
		unfilledPlaceholder,
		value,
	};
}

interface IProps {
	copy?: Partial<IAIPromptFieldCopy>;
	field: ReturnType<typeof useAIPromptField>;
	/** The textarea's id, for the caller's label. */
	id: string;
	impersonationContext?: Api.IImpersonationContext;
	placeholder?: string;
	/** Off for a setting saved as a draft, where a [placeholder] left in is the user's to finish later. */
	showPlaceholderError?: boolean;
	textAreaStyles?: StyleDeclarationValue[];
	usage: AIPromptUsage;
}

export const AIPromptField: React.FC<IProps> = ({
	copy: copyOverrides,
	field,
	id,
	impersonationContext,
	placeholder,
	showPlaceholderError = true,
	textAreaStyles = [],
	usage,
}) => {
	const copy = { ...DEFAULT_COPY, ...copyOverrides };
	const [isSavingPrompt, setIsSavingPrompt] = useState(false);
	const [isPanelOpen, setIsPanelOpen] = useState(false);

	const hasValue = field.value.trim().length > 0;
	const problem = showPlaceholderError ? field.placeholderProblem : null;
	const problemId = `${id}-problem`;

	return (
		<>
			<AIPromptBox>
				<AIPromptBoxToolbar>
					<HelpMeWriteMenu
						finalFocus={field.ref}
						impersonationContext={impersonationContext}
						onBrowseAll={() => setIsPanelOpen(true)}
						onPromptSelected={field.applyPrompt}
						usage={usage}
					/>
					{/* Only once the text is the user's own: an unedited prompt is already saved. */}
					{hasValue && !field.sourcePrompt ? (
						<AIPromptBoxToolbarEnd>
							<InputGroupButton onClick={() => setIsSavingPrompt(true)} size='sm'>
								<PlusIcon2 height={14} width={14} />
								Save as a prompt
							</InputGroupButton>
						</AIPromptBoxToolbarEnd>
					) : null}
				</AIPromptBoxToolbar>
				<AIPromptBoxTextarea
					aria-describedby={problem ? problemId : undefined}
					aria-invalid={!!problem}
					id={id}
					onChange={event => field.onChange(event.target.value)}
					placeholder={placeholder}
					ref={field.ref}
					styles={textAreaStyles}
					value={field.value}
				/>
			</AIPromptBox>
			{problem ? <FieldError id={problemId}>{problem}</FieldError> : null}
			{field.sourcePrompt ? (
				<AIPromptBoxCredit>
					<Pill color='gray' label={`Started from ${field.sourcePrompt.name}`} variant='tag' />
					<span>{copy.attributionNote}</span>
				</AIPromptBoxCredit>
			) : null}

			<SavePromptDialog
				description={copy.saveDescription}
				impersonationContext={impersonationContext}
				isOpen={isSavingPrompt}
				namePlaceholder={copy.saveNamePlaceholder}
				note={copy.saveNote}
				onOpenChange={setIsSavingPrompt}
				onSaved={field.onSaved}
				text={field.value.trim()}
				usage={usage}
			/>
			<AIPromptPanel
				description={hasValue ? copy.panelReplaceDescription : copy.panelDescription}
				impersonationContext={impersonationContext}
				isOpen={isPanelOpen}
				onOpenChange={setIsPanelOpen}
				onPromptSelected={field.applyPrompt}
				savedEmptyText={copy.savedEmptyText}
				usage={usage}
			/>
		</>
	);
};
